import {DEF_APP_THEME, Keys} from "../app/config/AppConfig";
import PropTypes from "prop-types";
import React, {createContext, useContext} from "react";
import {getDefStr, printError, printLog} from "./fusion-ui/utils/ReactUtils";

const ThemeContext = createContext(null);
ThemeContext.displayName = "ThemeContext";

/**
 * ProjectThemeProvider is a React functional component designed to manage and provide
 * theme-related values and styles to all child components within its context.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.themeName - The name of the theme to be applied.
 * @param {React.ReactNode} props.children - The child components that will receive the theme context.
 *
 * The component maintains a set of predefined CSS variables related to theme properties
 * such as colors, font-sizes, border-radii, shadows, and various component-specific styles.
 * These values are used to ensure consistency across the application's UI.
 *
 * The context provider passes down a combination of the theme name and these predefined
 * styles, allowing child components to access and apply them as needed.
 *
 * The theme values include:
 *   - Border radius values for different sizes (e.g., small, large)
 *   - Font sizes ranging from extra small (xs) to extra large (xxxl)
 *   - Primary, light, dark, and alternate color values (with multiple shades)
 *   - Text colors, including variations for black and white text
 *   - Ripple colors for interactive elements
 *   - Component-specific styles like dropdown menus and autocomplete dropdowns.
 *
 * Utility functions such as `log` and `logErr` are provided to streamline logging within the scope of this
 * theme provider, ensuring consistent tagging of logs and errors with the component's identifier.
 */
function ProjectThemeProvider({themeName, children}) {
    const TAG = "AppThemeProvider";
    const BG_COLOR = "var(--bodyColor)";
    const BG_ROOT_COLOR = "var(--bodyRootColor)";

    const themeValues = {
        //border-radius values
        borderRadius: ".6rem",
        borderRadiusSm: ".4rem",
        borderRadiusXs: ".2rem",
        borderRadiusLg: ".8rem",
        borderRadiusXl: "1.1rem",
        borderRadiusXxl: "1.6rem",

        //font-size
        fontSize: "1rem",
        fontSizeXs: "0.5rem",
        fontSizeMdXs: "0.575rem",
        fontSizeMdSm: "0.625rem",
        fontSizeSm: "0.75rem",
        fontSizeSmMd: "0.8rem",
        fontSizeMd: "0.875rem",
        fontSizeMdLg: "1.15rem",
        fontSizeLg: "1.25rem",
        fontSizeXl: "1.625rem",
        fontSizeXxl: "2rem",
        fontSizeXxxl: "2.75rem",
        fontSizeXxxxl: "3.375rem",
        fontSizeXxxxxl: "4.375rem",
        fontSizeXxxxxxl: "5.375rem",

        //theme-primary
        primary: "var(--primary)",
        primary05: "var(--primary05)",
        primary10: "var(--primary10)",
        primary10Opaque: "var(--primary10Opaque)",
        primary20: "var(--primary20)",
        primary30: "var(--primary30)",
        primary40: "var(--primary40)",
        primary50: "var(--primary50)",
        primary60: "var(--primary60)",
        primary70: "var(--primary70)",
        primary80: "var(--primary80)",
        primary90: "var(--primary90)",

        //theme-primary-light
        primaryLight: "var(--primaryLight)",
        primaryLight10: "var(--primaryLight10)",
        primaryLight20: "var(--primaryLight20)",
        primaryLight25: "var(--primaryLight25)",
        primaryLight30: "var(--primaryLight30)",
        primaryLight40: "var(--primaryLight40)",
        primaryLight50: "var(--primaryLight50)",
        primaryLight60: "var(--primaryLight60)",
        primaryLight70: "var(--primaryLight70)",
        primaryLight75: "var(--primaryLight75)",
        primaryLight80: "var(--primaryLight80)",
        primaryLight90: "var(--primaryLight90)",

        //theme-primary-dark
        primaryDark: "var(--primaryDark)",
        primaryDark10: "var(--primaryDark10)",
        primaryDark20: "var(--primaryDark20)",
        primaryDark25: "var(--primaryDark25)",
        primaryDark30: "var(--primaryDark30)",
        primaryDark40: "var(--primaryDark40)",
        primaryDark50: "var(--primaryDark50)",
        primaryDark60: "var(--primaryDark60)",
        primaryDark70: "var(--primaryDark70)",
        primaryDark75: "var(--primaryDark75)",
        primaryDark80: "var(--primaryDark80)",
        primaryDark90: "var(--primaryDark90)",

        //theme-primary-alt
        primaryAlt: "var(--primaryAlt)",
        primaryAlt10: "var(--primaryAlt10)",
        primaryAlt20: "var(--primaryAlt20)",
        primaryAlt30: "var(--primaryAlt30)",
        primaryAlt40: "var(--primaryAlt40)",
        primaryAlt50: "var(--primaryAlt50)",
        primaryAlt60: "var(--primaryAlt60)",
        primaryAlt70: "var(--primaryAlt70)",
        primaryAlt80: "var(--primaryAlt80)",
        primaryAlt90: "var(--primaryAlt90)",

        //theme-primary-alt-light
        primaryAltLight: "var(--primaryAltLight)",
        primaryAltLight10: "var(--primaryAltLight10)",
        primaryAltLight20: "var(--primaryAltLight20)",
        primaryAltLight25: "var(--primaryAltLight25)",
        primaryAltLight30: "var(--primaryAltLight30)",
        primaryAltLight40: "var(--primaryAltLight40)",
        primaryAltLight50: "var(--primaryAltLight50)",
        primaryAltLight60: "var(--primaryAltLight60)",
        primaryAltLight70: "var(--primaryAltLight70)",
        primaryAltLight75: "var(--primaryAltLight75)",
        primaryAltLight80: "var(--primaryAltLight80)",
        primaryAltLight90: "var(--primaryAltLight90)",

        //theme-primary-alt-dark
        primaryAltDark: "var(--primaryAltDark)",
        primaryAltDark10: "var(--primaryAltDark10)",
        primaryAltDark20: "var(--primaryAltDark20)",
        primaryAltDark25: "var(--primaryAltDark25)",
        primaryAltDark30: "var(--primaryAltDark30)",
        primaryAltDark40: "var(--primaryAltDark40)",
        primaryAltDark50: "var(--primaryAltDark50)",
        primaryAltDark60: "var(--primaryAltDark60)",
        primaryAltDark70: "var(--primaryAltDark70)",
        primaryAltDark75: "var(--primaryAltDark75)",
        primaryAltDark80: "var(--primaryAltDark80)",
        primaryAltDark90: "var(--primaryAltLight90)",

        //theme-primaryBlue
        primaryBlue: "var(--primaryBlue)",
        primaryBlue10Opaque: "var(--primaryBlue10Opaque)",
        primaryBlue10: "var(--primaryBlue10)",
        primaryBlue20: "var(--primaryBlue20)",
        primaryBlue30: "var(--primaryBlue30)",
        primaryBlue40: "var(--primaryBlue40)",
        primaryBlue50: "var(--primaryBlue50)",
        primaryBlue60: "var(--primaryBlue60)",
        primaryBlue70: "var(--primaryBlue70)",
        primaryBlue80: "var(--primaryBlue80)",
        primaryBlue90: "var(--primaryBlue90)",

        //theme-primaryBlue-light
        primaryBlueLight: "var(--primaryBlueLight)",
        primaryBlueLight10: "var(--primaryBlueLight10)",
        primaryBlueLight20: "var(--primaryBlueLight20)",
        primaryBlueLight30: "var(--primaryBlueLight30)",
        primaryBlueLight40: "var(--primaryBlueLight40)",
        primaryBlueLight50: "var(--primaryBlueLight50)",
        primaryBlueLight60: "var(--primaryBlueLight60)",
        primaryBlueLight70: "var(--primaryBlueLight70)",
        primaryBlueLight80: "var(--primaryBlueLight80)",
        primaryBlueLight90: "var(--primaryBlueLight90)",

        //theme-primaryBlue-dark
        primaryBlueDark: "var(--primaryBlueDark)",
        primaryBlueDark10: "var(--primaryBlueDark10)",
        primaryBlueDark20: "var(--primaryBlueDark20)",
        primaryBlueDark30: "var(--primaryBlueDark30)",
        primaryBlueDark40: "var(--primaryBlueDark40)",
        primaryBlueDark50: "var(--primaryBlueDark50)",
        primaryBlueDark60: "var(--primaryBlueDark60)",
        primaryBlueDark70: "var(--primaryBlueDark70)",
        primaryBlueDark80: "var(--primaryBlueDark80)",
        primaryBlueDark90: "var(--primaryBlueDark90)",

        //ripple-color
        rippleColor: "var(--rippleColor)",
        rippleColorXs: "var(--rippleColorXs)",
        rippleColorLg: "var(--rippleColorLg)",
        rippleColorXl: "var(--rippleColorXl)",
        rippleColorPrimary: "var(--primary25)",
        rippleColorXsPrimary: "var(--primary15)",
        rippleColorLgPrimary: "var(--primary50)",
        rippleColorXlPrimary: "var(--primary70)",
        rippleColorPrimaryAlt: "var(--primaryAlt20)",
        rippleColorXsPrimaryAlt: "var(--primaryAlt10)",
        rippleColorLgPrimaryAlt: "var(--primaryAlt50)",
        rippleColorXlPrimaryAlt: "var(--primaryAlt70)",

        //text-color
        text: "var(--textColor)",
        text05: "var(--textColor05)",
        text10: "var(--textColor10)",
        text20: "var(--textColor20)",
        text30: "var(--textColor30)",
        text40: "var(--textColor40)",
        text50: "var(--textColor50)",
        text60: "var(--textColor60)",
        text70: "var(--textColor70)",
        text80: "var(--textColor80)",
        text90: "var(--textColor90)",

        //text-white-color
        textBlack: "var(--textColorBlack)",
        textBlack05: "var(--textBlackColor05)",
        textBlack10: "var(--textColorBlack10)",
        textBlack20: "var(--textColorBlack20)",
        textBlack30: "var(--textColorBlack30)",
        textBlack40: "var(--textColorBlack40)",
        textBlack50: "var(--textColorBlack50)",
        textBlack60: "var(--textColorBlack60)",
        textBlack70: "var(--textColorBlack70)",
        textBlack80: "var(--textColorBlack80)",
        textBlack90: "var(--textColorBlack90)",

        //text-dark-color
        textWhite: "var(--textColorWhite)",
        textWhite05: "var(--textWhiteColor05)",
        textWhite10: "var(--textColorWhite10)",
        textWhite20: "var(--textColorWhite20)",
        textWhite30: "var(--textColorWhite30)",
        textWhite40: "var(--textColorWhite40)",
        textWhite50: "var(--textColorWhite50)",
        textWhite60: "var(--textColorWhite60)",
        textWhite70: "var(--textColorWhite70)",
        textWhite80: "var(--textColorWhite80)",
        textWhite90: "var(--textColorWhite90)",

        t: "transparent",
        transparent: "transparent",

        //various
        bg: BG_COLOR,
        bgRoot: BG_ROOT_COLOR,

        //specific values

        //shadow
        boxShadow: `2px 2px 26px var(--primary20)`,
        boxShadowXs: `1px 1px 6px var(--primary10)`,
        boxShadowSm: `2px 2px 10px 6px var(--primary15)`,
        boxShadowMdSm: `0px 0px 8px 6px var(--primary05)`,
        boxShadowMd: `2px 2px 18px 6px var(--primary20)`,
        boxShadowLg: `2px 4px 40px 6px var(--primary40)`,
        boxShadowXl: `2px 4px 40px 6px var(--primary60)`,
        boxShadowXxl: `2px 4px 40px 6px var(--primary80)`,

        //component values
        dropdownMenuStyle: {
            boxShadow: `2px 2px 26px var(--primary20)`,
            borderRadius: "1rem",
            width: "auto",
            padding: ".4rem .6rem .7rem .6rem",
            backgroundColor: BG_COLOR,
            // backgroundColor: 'yellow !important',
        },
        autoCompleteDropdownStyles: {
            boxShadow: `0px 2px 8px var(--textColor20)`,
            // boxShadow: "var(--shadow)",
            backgroundColor: BG_COLOR,
            borderRadius: ".6rem",
            width: "auto",
            padding: ".4rem .6rem .7rem .6rem",
        },
        dropdownMenuItemStyle: {},
    };

    function getInputStyles() {
        return {};
    }

    //Get theme name from subdomainData & set predefined-styles-css file in  DOM
    // const themeName = getJsonValue(subdomainData, Keys.theme, DEF_APP_THEME);
    // const themeName = getDefStr(appThemeName, DEF_APP_THEME);

    // return <ThemeContext.Provider value={{[Keys.theme]: themeName, ...themeValues}}>
    return <ThemeContext.Provider value={{
        [Keys.theme]: getDefStr(themeName, DEF_APP_THEME),
        ...themeValues,
    }}>
        {children}
    </ThemeContext.Provider>;

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
}

ProjectThemeProvider.propTypes = {
    themeName: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

/**
 * Custom React hook to retrieve the current theme from ThemeContext.
 * This hook uses the useContext hook internally to access the ThemeContext.
 *
 * @returns {Object} The current theme object from ThemeContext.
 *
 * @example
 * const theme = useTheme();
 * console.log(theme.primaryColor); // Outputs the primary color of the theme
 */
function useTheme() {
    return useContext(ThemeContext);
}

export {ProjectThemeProvider, useTheme};
